import { useState } from "react";
import axios from "axios";
import { X, ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadFormModal — reusable inline lead capture form.
 * Opens from "Get Started" on pricing cards or any CTA that needs a quick form.
 * Posts to the same /api/public/contact-submit endpoint as the homepage contact section.
 *
 * Props:
 *  - open: boolean
 *  - onClose(): dismiss
 *  - presetInterest: string — seeds the "interest" field (e.g. the tier name)
 *  - title: heading text
 */
const LeadFormModal = ({ open, onClose, presetInterest = "", title = "Talk to our team" }) => {
  const [formData, setFormData] = useState({
    instituteName: "",
    yourName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/public/contact-submit`, {
        institute_name: formData.instituteName,
        your_name: formData.yourName,
        email: formData.email,
        phone: formData.phone,
        content_type: presetInterest,
        message: formData.message,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    // reset on close so next open is fresh
    setFormData({ instituteName: "", yourName: "", email: "", phone: "", message: "" });
    setSubmitted(false);
    setError("");
    onClose?.();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-modal-title"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={close}
      data-testid="lead-form-modal"
    >
      <div
        style={{
          background: "#fff", borderRadius: 24, width: "100%", maxWidth: 540,
          padding: "32px 32px 28px", boxShadow: "0 40px 80px -20px rgba(0,0,0,0.3)",
          maxHeight: "90vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            {presetInterest && (
              <span className="chip" style={{ background: "var(--mint-100)", color: "var(--mint-800)", borderColor: "transparent", marginBottom: 10 }}>
                {presetInterest}
              </span>
            )}
            <h2 id="lead-modal-title" style={{ fontFamily: "Clash Display, sans-serif", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--ink)", margin: 0 }}>
              {title}
            </h2>
          </div>
          <button onClick={close} style={{ background: "none", border: "none", padding: 6, borderRadius: 8, cursor: "pointer" }} aria-label="Close" data-testid="lead-form-close">
            <X size={20} />
          </button>
        </div>

        {submitted ? (
          <div style={{ textAlign: "center", padding: "32px 0 12px" }}>
            <div style={{ width: 64, height: 64, margin: "0 auto 16px", borderRadius: "50%", background: "var(--mint-100)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircle2 size={32} strokeWidth={2} color="var(--mint-700)" />
            </div>
            <h3 style={{ fontFamily: "Clash Display, sans-serif", fontSize: 22, margin: "0 0 8px", color: "var(--ink)" }}>Thanks — we'll be in touch.</h3>
            <p style={{ color: "var(--ink-3)", fontSize: 15, lineHeight: 1.6, margin: "0 0 24px" }}>
              Our team will reach out within 24 hours with a custom plan for your institute.
            </p>
            <button onClick={close} className="btn-pill-primary btn-pill-lg" style={{ margin: "0 auto" }} data-testid="lead-form-close-confirm">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ marginTop: 20 }}>
            <p style={{ color: "var(--ink-3)", fontSize: 14.5, lineHeight: 1.6, marginBottom: 20 }}>
              Share a few details and we'll put together a tailored proposal.
            </p>
            <div style={{ display: "grid", gap: 12 }}>
              <label style={{ display: "block" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Institute name</span>
                <input
                  required
                  value={formData.instituteName}
                  onChange={(e) => setFormData({ ...formData, instituteName: e.target.value })}
                  style={{ marginTop: 4, width: "100%", border: "1px solid var(--line)", borderRadius: 12, padding: "12px 14px", fontSize: 14, background: "#fff" }}
                  data-testid="lead-form-institute"
                />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <label style={{ display: "block" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Your name</span>
                  <input
                    required
                    value={formData.yourName}
                    onChange={(e) => setFormData({ ...formData, yourName: e.target.value })}
                    style={{ marginTop: 4, width: "100%", border: "1px solid var(--line)", borderRadius: 12, padding: "12px 14px", fontSize: 14, background: "#fff" }}
                    data-testid="lead-form-name"
                  />
                </label>
                <label style={{ display: "block" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Phone</span>
                  <input
                    required
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{ marginTop: 4, width: "100%", border: "1px solid var(--line)", borderRadius: 12, padding: "12px 14px", fontSize: 14, background: "#fff" }}
                    data-testid="lead-form-phone"
                  />
                </label>
              </div>
              <label style={{ display: "block" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Email</span>
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{ marginTop: 4, width: "100%", border: "1px solid var(--line)", borderRadius: 12, padding: "12px 14px", fontSize: 14, background: "#fff" }}
                  data-testid="lead-form-email"
                />
              </label>
              <label style={{ display: "block" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Message <span style={{ textTransform: "none", fontWeight: 400, color: "var(--ink-4)" }}>(optional)</span></span>
                <textarea
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={presetInterest ? `Tell us about your interest in ${presetInterest}…` : "Anything we should know?"}
                  style={{ marginTop: 4, width: "100%", border: "1px solid var(--line)", borderRadius: 12, padding: "12px 14px", fontSize: 14, background: "#fff", resize: "vertical", fontFamily: "inherit" }}
                  data-testid="lead-form-message"
                />
              </label>
            </div>
            {error && (
              <div style={{ background: "#FEE2E2", color: "#991B1B", padding: "10px 14px", borderRadius: 10, marginTop: 16, fontSize: 13 }}>{error}</div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-pill-primary btn-pill-full btn-pill-lg"
              style={{ marginTop: 20 }}
              data-testid="lead-form-submit"
            >
              {submitting ? "Sending…" : "Send message"} <ArrowRight size={18} strokeWidth={2.5} />
            </button>
            <p style={{ fontSize: 12, color: "var(--ink-4)", textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
              We typically reply within 24 hours. No spam, ever.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LeadFormModal;
